import { useSiteSetupActions } from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { DateRangePicker } from "../../../components";

const DemoOperationalActualsSection = ({
  populateDemoActuals,
}: {
  populateDemoActuals: ReturnType<
    typeof useSiteSetupActions
  >["populateDemoActuals"];
}) => {
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [scalingFactor, setScalingFactor] = useState("1.0");
  const [variationPercent, setVariationPercent] = useState("5.0");

  const [result, setResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleMonthChange = ({
    start,
    end,
  }: {
    start: Date | null;
    end: Date | null;
  }) => {
    if (start && end) {
      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
      const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}`;
      setStartMonth(startStr);
      setEndMonth(endStr);
    }
  };

  const handleSubmit = async () => {
    setResult(null);
    try {
      const response = await populateDemoActuals.mutateAsync({
        startMonth,
        endMonth,
        scalingFactor: parseFloat(scalingFactor),
        variationPercent: parseFloat(variationPercent),
        seed: 42,
      });
      setResult({
        type: "success",
        message: `Wrote ${response.monthsWritten} months of demo readings. You can now generate readings files / invoices for these months.`,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setResult({ type: "error", message: errorMessage });
    }
  };

  const isValid = startMonth && endMonth && scalingFactor && variationPercent;

  return (
    <div>
      <h6 className="fw-bold mb-2">Demo actuals</h6>
      <p className="text-muted mb-2" style={{ fontSize: "0.875rem" }}>
        Populate readings from forecast data with scaling and random variation
        per month.
      </p>
      <Form>
        <Row className="mb-2">
          <Col md={6}>
            <Form.Label>Month Range</Form.Label>
            <DateRangePicker
              value={{
                start: startMonth ? new Date(`${startMonth}-01`) : null,
                end: endMonth ? new Date(`${endMonth}-01`) : null,
              }}
              onChange={handleMonthChange}
              mode="month"
              startPlaceholder="Start month"
              endPlaceholder="End month"
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={4} md={2}>
            <Form.Label>Scaling factor</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={scalingFactor}
              onChange={(e) => setScalingFactor(e.target.value)}
            />
          </Col>
          <Col xs={4} md={2}>
            <Form.Label>Variation %</Form.Label>
            <Form.Control
              type="number"
              step="1"
              value={variationPercent}
              onChange={(e) => setVariationPercent(e.target.value)}
            />
          </Col>
        </Row>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={populateDemoActuals.isLoading || !isValid}
        >
          {populateDemoActuals.isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Populating...
            </>
          ) : (
            "Populate demo actuals"
          )}
        </Button>
      </Form>
      {result && (
        <Alert
          variant={result.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setResult(null)}
          className="mt-2 mb-0"
        >
          {result.message}
        </Alert>
      )}
    </div>
  );
};

export default DemoOperationalActualsSection;
